// Batch numbers and expiry dates as printed on a pack.
//
// TODO(clinical-verify): the expiry rules below are the approved defaults
// (docs/plans/phase1-stock.md §12, decision 4) awaiting a pharmacist's confirmation:
//   - a printed month and year ("03/2027") means the last day of that month;
//   - a batch is usable through its expiry date, inclusive, in East Africa Time.
// The conservative consequence ships regardless: once a batch is past that date it cannot
// be issued, transferred or dispensed, only written off.

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import type { CalendarDay } from '../time.ts';
import { compareDays, daysInMonth, isValidDay } from '../time.ts';

export const MAX_BATCH_NUMBER_LENGTH = 64;

export type BatchErrorCode =
  | 'batch.empty'
  | 'batch.too_long'
  | 'batch.expiry_empty'
  | 'batch.expiry_malformed'
  | 'batch.expiry_impossible';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const BATCH_ERRORS: Record<BatchErrorCode, string> = {
  'batch.empty': 'No batch number was entered',
  'batch.too_long': `A batch number has at most ${MAX_BATCH_NUMBER_LENGTH} characters`,
  'batch.expiry_empty': 'No expiry date was entered',
  'batch.expiry_malformed': 'Write the expiry as on the pack: 03/2027, 2027-03 or 15/03/2027',
  'batch.expiry_impossible': 'That expiry date does not exist',
};

export interface NormalisedBatchNumber {
  /** Trimmed, as typed otherwise: what is shown and printed. */
  readonly display: string;
  /** Upper case with internal whitespace collapsed: what is matched on. */
  readonly key: string;
}

/**
 * Batch numbers are matched case-insensitively with runs of whitespace treated as one
 * space. Hyphens, slashes and single spaces are kept: "AB-12" and "AB12" may be different
 * batches, and guessing that they are the same would merge two batches' stock.
 */
export function normaliseBatchNumber(text: string): Result<NormalisedBatchNumber, BatchErrorCode> {
  const display = text.trim().replace(/\s+/g, ' ');
  if (display === '') return err('batch.empty');
  if (display.length > MAX_BATCH_NUMBER_LENGTH) return err('batch.too_long');
  return ok({ display, key: display.toUpperCase() });
}

export type ExpiryPrecision = 'day' | 'month';

export interface BatchExpiry {
  /** The last day the batch may be used. */
  readonly day: CalendarDay;
  /** Whether the pack printed a day, or only a month (stored as the month's last day). */
  readonly precision: ExpiryPrecision;
}

/**
 * Reads an expiry date the way packs print them:
 * - `2027-03-15` and `15/03/2027` (day, month, year, as written in Kenya): that day;
 * - `03/2027`, `3/2027`, `03/27` and `2027-03`: the last day of that month.
 * Two-digit years are 20YY. `-` and `.` are accepted in place of `/`.
 */
export function parseBatchExpiry(text: string): Result<BatchExpiry, BatchErrorCode> {
  const trimmed = text.trim();
  if (trimmed === '') return err('batch.expiry_empty');

  let match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  if (match) return dayExpiry(Number(match[1]), Number(match[2]), Number(match[3]));

  match = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(trimmed);
  if (match) return dayExpiry(Number(match[3]), Number(match[2]), Number(match[1]));

  match = /^(\d{4})-(\d{1,2})$/.exec(trimmed);
  if (match) return monthExpiry(Number(match[1]), Number(match[2]));

  match = /^(\d{1,2})[/.-](\d{4}|\d{2})$/.exec(trimmed);
  if (match) {
    const yearText = match[2] ?? '';
    const year = yearText.length === 2 ? 2000 + Number(yearText) : Number(yearText);
    return monthExpiry(year, Number(match[1]));
  }

  return err('batch.expiry_malformed');
}

function dayExpiry(year: number, month: number, day: number): Result<BatchExpiry, BatchErrorCode> {
  const value = { year, month, day };
  if (!isValidDay(value)) return err('batch.expiry_impossible');
  return ok({ day: value, precision: 'day' });
}

function monthExpiry(year: number, month: number): Result<BatchExpiry, BatchErrorCode> {
  if (month < 1 || month > 12) return err('batch.expiry_impossible');
  return ok({ day: { year, month, day: daysInMonth(year, month) }, precision: 'month' });
}

/**
 * Whether a batch may be issued on a given EAT calendar day. A batch with no expiry (an
 * item the catalogue marks as not expiry-tracked) is always usable.
 */
export function isBatchUsableOn(expiry: CalendarDay | null, onDay: CalendarDay): boolean {
  if (expiry === null) return true;
  return compareDays(onDay, expiry) <= 0;
}
